import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NavigationGuardProvider } from './context/NavigationGuardContext';
import { BranchProvider } from './context/BranchContext';
import { DesktopLayoutProvider } from './context/DesktopLayoutContext';
import LoginScreen from './components/LoginScreen';
import SnapshotScreen from './components/reports/SnapshotScreen';
import SalesScreen from './components/reports/SalesScreen';
import ReportsScreen from './components/ReportsScreen';
import AIScreen from './components/AIScreen';
import OwnerViewLandingPage from './components/OwnerViewLandingPage';
import { OwnerViewProvider, getOwnerLocationCount } from './context/OwnerViewContext';
import ProductMenuPerformanceScreen from './components/reports/ProductMenuPerformanceScreen';
import OffersReportScreen from './components/reports/OffersReportScreen';
import DesktopLayout from './components/layout/DesktopLayout';
import { CateringToastContainer } from './components/catering/components/ui/Toast';
import CheckinReport from './components/reports/CheckinReport';
import LaborReport from './components/reports/LaborReport';
import LoyaltyReport from './components/reports/LoyaltyReport';
import CustomersReport from './components/reports/CustomersReport';
import EODSummaryReport from './components/reports/EODSummaryReport';
import ReservationReport from './components/reports/ReservationReport';
import EventReport from './components/reports/EventReport';
import ReportsDocsPage from './components/reports/ReportsDocsPage';
import EmployeeLayout, { EmployeeListPage, EmployeeAddPage, EmployeeEditPage, EmployeeViewPage } from './components/employees/EmployeeManagement';
import LoyaltyLayout from './components/LoyaltyLayout';
import LoyaltyAccessRestricted from './components/loyalty/components/layout/LoyaltyAccessRestricted';
import LoyaltyMobileHome from './components/loyalty/LoyaltyMobileHome';
import DisplayListPage from './components/display/features/DisplayListPage/DisplayListPage';
import MediaLibraryPage from './components/display/features/MediaLibraryPage/MediaLibraryPage';
import PlaylistFormPage from './components/display/features/PlaylistFormPage/PlaylistFormPage';
import CreateDisplay from './components/display/features/CreateDisplay/CreateDisplay';
import OffersListPage from './components/offers/OffersListPage';
import OfferForm from './components/offers/OfferForm';
import OfferDetail from './components/offers/OfferDetail';
// Reference components for mobile and desktop report screens
import CheckinReportDesktop from './components/reference/CheckinReportDesktop';
import CheckinReportMobile from './components/reference/checkinm';
import CustomersReportdesktop from './components/reference/customerd';
import CustomersReportMobile from './components/reference/customerm';
import MobileV61 from './components/reference/mobile-v6-1';
import DesktopV61 from './components/reference/desktop-v6-1';
import ProductV61 from './components/reference/proudct-v6-1';
import PaymentsAccountingDesktop from './components/reference/eodd';
import PaymentsAccountingMobile from './components/reference/eodm';
import LabourReportDesktop from './components/reference/labourd';
import LabourReportMobile from './components/reference/labourm';
import ReservationDashboard from './components/reference/reservationdm';
import EventsPreOrderDesktop from './components/reference/eventd';
import EventsPreOrderMobile from './components/reference/eventm';
import MobileSnapSaleProd from './components/reference/mobile-snap-sale-prod';
import SalesReference from './components/reference/sales';
import SalesReportDesktop from './components/reference/sales-desktop';
import MultiOutletLanding from './components/reference/multi-outletlanding';
import ReferenceRouteMapPage from './components/reference/ReferenceRouteMapPage';
import ChatbotReports from './components/reports/ChatbotReports';
import ProductCatalogToastContainer from './components/productCatalog/components/ui/ProductCatalogToastContainer';
import OffersToastContainer from './components/offers/components/ui/OffersToastContainer';
import { enableChatbotReportsBoolean } from './constants/ChatbotReportsConstants';
import './App.css';
import CateringHubPage from './components/catering/features/CateringHubPage';

const CHATBOT_ROUTES = ['/owner-view', '/ai', '/reports', '/reports/snapshot', '/reports/sales', '/reports/product', '/reports/checkin', '/reports/labor', '/reports/loyalty', '/reports/customers', '/reports/eod', '/reports/reservation', '/reports/events'];

// Product Catalog feature pages (lazy loaded)
const PCItemsPage = lazy(() => import('./components/productCatalog/features/items/ItemsPage'));
const PCItemFormPage = lazy(() => import('./components/productCatalog/features/items/ItemFormPage'));
const PCMenusPage = lazy(() => import('./components/productCatalog/features/menus/MenusPage'));
const PCMenuFormPage = lazy(() => import('./components/productCatalog/features/menus/MenuFormPage'));
const PCCategoriesPage = lazy(() => import('./components/productCatalog/features/categories/CategoriesPage'));
const PCCategoryFormPage = lazy(() => import('./components/productCatalog/features/categories/CategoryFormPage'));
const PCModifiersPage = lazy(() => import('./components/productCatalog/features/modifiers/ModifiersPage'));
const PCModifierFormPage = lazy(() => import('./components/productCatalog/features/modifiers/ModifierFormPage'));
const PCVariationSetsPage = lazy(() => import('./components/productCatalog/features/optionSets/OptionSetsPage'));
const PCVariationSetFormPage = lazy(() => import('./components/productCatalog/features/optionSets/OptionSetFormPage'));
const PCMenuPriceEditorPage = lazy(() => import('./components/productCatalog/features/menus/MenuPriceEditorPage'));
const PCMenuPerformanceReport = lazy(() => import('./components/productCatalog/features/menus/MenuPerformanceReport'));
const PCCombosPage = lazy(() => import('./components/productCatalog/features/combos/CombosPage'));
const PCComboFormPage = lazy(() => import('./components/productCatalog/features/combos/ComboFormPage'));
const PCComboReportPage = lazy(() => import('./components/productCatalog/features/combos/ComboReportPage'));
const PCSetMenusPage = lazy(() => import('./components/productCatalog/features/setMenus/SetMenusPage'));
const PCAvailabilityPage = lazy(() => import('./components/productCatalog/features/availability/AvailabilityControlCentre'));
const PCSetMenuFormPage = lazy(() => import('./components/productCatalog/features/setMenus/SetMenuFormPage'));
const PCKitchenStationsPage = lazy(() => import('./components/productCatalog/features/kitchenStations/KitchenStationsPage'));
const PCKitchenStationFormPage = lazy(() => import('./components/productCatalog/features/kitchenStations/KitchenStationFormPage'));
const PCSessionsPage = lazy(() => import('./components/productCatalog/features/sessions/SessionsPage'));
const PCSessionFormPage = lazy(() => import('./components/productCatalog/features/sessions/SessionFormPage'));
const PCSyncHistoryPage = lazy(() => import('./components/productCatalog/features/syncHistory/SyncHistoryPage'));
const PCInventoryHubPage = lazy(() => import('./components/productCatalog/features/InventoryHubPage'));

// Catering feature pages (lazy loaded)
const CateringSettingsPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/settings/CateringSettingsPage'));
const CateringEmailTemplatesPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/settings/EmailTemplatesPage'));
const CateringBeoTemplateEditor = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/settings/BeoTemplateEditor'));
const CateringPackagesPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/packages/PackagesPage'));
const CateringPackageFormPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/packages/PackageFormPage'));
const CateringOrdersPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/OrdersPage'));
const CateringOrderFormPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/OrderFormPage'));
const CateringOrderDetailPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/OrderDetailPage'));
const CateringOrderBEOPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/BanquetEventOrder'));
const CateringOrderPackSheetPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/PackSheet'));
const CateringContainerLabelsPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/ContainerLabels'));
const CateringPrepListPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/PrepListPage'));
const CateringRecurringOrdersPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/RecurringOrdersPage'));
const CateringRecurringOrderFormPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/orders/RecurringOrderFormPage'));
const CateringInvoicesPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/invoices/InvoicesPage'));
const CateringInvoiceFormPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/invoices/InvoiceFormPage'));
const CateringInvoiceDetailPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/invoices/InvoiceDetailPage'));
const CateringInvoiceReviewPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/invoices/ReviewInvoicePage'));
const CateringLeadsPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/leads/LeadsPage'));
const CateringLeadFormPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/leads/LeadFormPage'));
const CateringLeadDetailPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/leads/LeadDetailPage'));
const CateringInquiryFormConfig = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/leads/InquiryFormConfig'));
const CateringEstimatesPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/estimates/EstimatesPage'));
const CateringEstimateFormPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/estimates/EstimateFormPage'));
const CateringEstimateDetailPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/estimates/EstimateDetailPage'));
const CateringCustomersPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/customers/CustomersPage'));
const CateringCustomerDetailPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/customers/CustomerDetailPage'));
const CateringCustomerFormPage = lazy(() => import(/* webpackChunkName: "catering", webpackPrefetch: true */ './components/catering/features/customers/CustomerFormPage'));
const CateringCustomerEstimatePage = lazy(() => import(/* webpackChunkName: "catering-public" */ './components/catering/features/estimates/customer/CustomerEstimatePage'));

// Loyalty feature pages (lazy loaded)
const ProgramSettings = lazy(() => import('./components/loyalty/features/program/ProgramSettings'));
const ProgramSetup = lazy(() => import('./components/loyalty/features/program/ProgramSetup'));
const RewardsCatalog = lazy(() => import('./components/loyalty/features/rewards/RewardsCatalog'));
const RewardForm = lazy(() => import('./components/loyalty/features/rewards/RewardForm'));
const MembersPage = lazy(() => import('./components/loyalty/features/members/MembersPage'));
const MemberProfile = lazy(() => import('./components/loyalty/features/members/MemberProfile'));
const NotificationsList = lazy(() => import('./components/loyalty/features/notifications/NotificationsList'));
const AutomatedRewardsList = lazy(() => import('./components/loyalty/features/automated-rewards/AutomatedRewardsList'));

const shimmerStyle = `
@keyframes appShimmer {
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
`;
const ShimmerBone = ({ w = '100%', h = 14, r = 6, style = {} }: { w?: number | string; h?: number; r?: number; style?: React.CSSProperties }) => (
    <div style={{
        width: w, height: h, borderRadius: r,
        background: 'linear-gradient(90deg, #F0F1F5 25%, #E2E5EC 50%, #F0F1F5 75%)',
        backgroundSize: '800px 100%',
        animation: 'appShimmer 1.5s ease-in-out infinite',
        ...style,
    }} />
);
const LoyaltyLoader = () => {
    const isDesktop = useIsDesktop();
    const shimmerCards = [0, 1, 2].map(i => (
        <div key={i} style={{
            background: '#fff', borderRadius: 14, border: '1px solid #E2E5EC',
            padding: 18, marginBottom: 12,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <ShimmerBone w={40} h={40} r={10} />
                <div style={{ flex: 1 }}>
                    <ShimmerBone w="60%" h={14} r={6} style={{ marginBottom: 6 }} />
                    <ShimmerBone w="40%" h={10} r={4} />
                </div>
                <ShimmerBone w={60} h={24} r={12} />
            </div>
            <ShimmerBone w="90%" h={10} r={4} style={{ marginBottom: 6 }} />
            <ShimmerBone w="70%" h={10} r={4} />
        </div>
    ));
    const inner = (
        <div style={{ padding: 20, maxWidth: 800, margin: '0 auto' }}>
            <style>{shimmerStyle}</style>
            <ShimmerBone w={180} h={22} r={8} style={{ marginBottom: 8 }} />
            <ShimmerBone w={120} h={12} r={6} style={{ marginBottom: 24 }} />
            {shimmerCards}
        </div>
    );
    if (isDesktop) return <DesktopLayout>{inner}</DesktopLayout>;
    return inner;
};
const L = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<LoyaltyLoader />}>{children}</Suspense>;

/** Catering Suspense wrapper — null fallback prevents the LoyaltyLoader flash
 *  during SPA navigation between catering pages. All catering pages share one
 *  prefetched chunk so the fallback is only ever visible for a brief instant on
 *  the very first cold load; null keeps the previous screen visible instead of
 *  flashing the wrong (Loyalty-style) shimmer. */
const C = ({ children }: { children: React.ReactNode }) => <Suspense fallback={null}>{children}</Suspense>;

const useIsDesktop = () => {
    const query = '(min-width: 900px)';
    const getMatches = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
    const [isDesktop, setIsDesktop] = useState(getMatches);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const media = window.matchMedia(query);
        const onChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
        setIsDesktop(media.matches);
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    return isDesktop;
};

/** Product Catalog shimmer — real text for labels, shimmer only for API data rows. */
const PCLoaderFallback = () => {
    const isDesktop = useIsDesktop();
    if (!isDesktop) {
        return (
            <div style={{ background: '#F5F6FA', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
                <style>{shimmerStyle}</style>
                {/* Real header text */}
                <div style={{ position: 'sticky', top: 0, zIndex: 50, background: '#fff', borderBottom: '1px solid #E2E5EC', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 22, color: '#1A1D2E' }}>arrow_back</span>
                    <span style={{ fontSize: 18, fontWeight: 700, color: '#1A1D2E' }}>Product Catalog</span>
                </div>
                {/* Real search label, shimmer for data only */}
                <div style={{ padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E5EC', padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 18, color: '#9CA3B8' }}>search</span>
                        <span style={{ fontSize: 13, color: '#9CA3B8' }}>Search...</span>
                    </div>
                    {/* Shimmer ONLY for data rows */}
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E5EC', padding: '14px 16px', marginBottom: 8 }}>
                            <ShimmerBone w={`${60 + i * 5}%`} h={14} r={5} style={{ marginBottom: 6 }} />
                            <ShimmerBone w={`${40 + i * 3}%`} h={10} r={4} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return (
        <DesktopLayout>
            <div style={{ padding: 25 }}>
                <style>{shimmerStyle}</style>
                {/* Real page header — icon + static text */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #D23A3B, #E86B6B)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 22, color: 'rgba(255,255,255,0.95)' }}>inventory_2</span>
                    </div>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#1A1D2E', fontFamily: "'DM Sans', sans-serif" }}>Product Catalog</div>
                        <div style={{ fontSize: 12, color: '#9CA3B8', marginTop: 2 }}>Loading items...</div>
                    </div>
                </div>
                {/* Real search bar */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                    <div style={{ width: 300, background: '#fff', borderRadius: 10, border: '1px solid #E2E5EC', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 18, color: '#9CA3B8' }}>search</span>
                        <span style={{ fontSize: 13, color: '#9CA3B8' }}>Search...</span>
                    </div>
                    <div style={{ width: 140, background: '#F0F1F5', borderRadius: 20, border: '1px solid #E2E5EC', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 13, color: '#9CA3B8' }}>All Types</span>
                        <span className="material-symbols-rounded" style={{ fontSize: 14, color: '#9CA3B8' }}>expand_more</span>
                    </div>
                </div>
                {/* Shimmer ONLY for data rows (API content) */}
                {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} style={{
                        background: '#fff', borderRadius: 14, border: '1px solid #E2E5EC',
                        padding: '14px 18px', marginBottom: 10,
                        display: 'flex', alignItems: 'center', gap: 14,
                    }}>
                        <ShimmerBone w={18} h={18} r={4} />
                        <div style={{ flex: 1 }}>
                            <ShimmerBone w={`${55 + i * 5}%`} h={14} r={5} style={{ marginBottom: 6 }} />
                            <ShimmerBone w={`${35 + i * 3}%`} h={10} r={4} />
                        </div>
                        <ShimmerBone w={80} h={12} r={4} />
                        <ShimmerBone w={60} h={12} r={4} />
                    </div>
                ))}
            </div>
        </DesktopLayout>
    );
};
const PC = ({ children }: { children: React.ReactNode }) => <Suspense fallback={<PCLoaderFallback />}>{children}</Suspense>;

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    if (!isAuthenticated) {
        const dest = location.pathname + location.search;
        if (dest !== '/' && dest !== '/login') sessionStorage.setItem('auth_redirect', dest);
        return <Navigate to="/login" replace />;
    }
    return children;
};

const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];

const LOYALTY_ALLOWED_ROLES = ['Restaurant_Owner', 'Restaurant_Manager', 'Regional_Employee'];

const LoyaltyAccessRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, isAuthLoading, auth } = useAuth();
    if (isAuthLoading) return null;
    if (!isAuthenticated) return <Navigate to="/login" replace />;
    const role = auth?.role ?? auth?.designation ?? '';
    return LOYALTY_ALLOWED_ROLES.includes(role) ? <>{children}</> : <LoyaltyAccessRestricted />;
};

const AppRoutes = () => {
    const { isAuthenticated, auth } = useAuth();
    const isDesktop = useIsDesktop();
    const location = useLocation();
    const userRole = auth?.role ?? '';
    const isSuperUser = SUPER_ROLES.includes(userRole);
    const renderReportsChatbot = isAuthenticated && CHATBOT_ROUTES.includes(location.pathname);

    const homePath = isSuperUser ? '/owner-view' : '/reports/snapshot';
    const navigate = useNavigate();

    // Handle auto-redirect after auth restores from secure storage on reload.
    // Must be a useEffect (not inline JSX) — React 18 Strict Mode double-renders
    // would call inline sessionStorage reads twice, consuming the key on the first
    // render and falling back to homePath on the second.
    useEffect(() => {
        if (!isAuthenticated || location.pathname !== '/login') return;
        const saved = sessionStorage.getItem('auth_redirect');
        sessionStorage.removeItem('auth_redirect');
        navigate(saved && saved !== '/login' ? saved : homePath, { replace: true });
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <BranchProvider>
            <DesktopLayoutProvider>
                <div style={{
                    minHeight: '100vh',
                    height: '100%',
                    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    WebkitFontSmoothing: 'antialiased',
                }}>
                    <link
                        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap"
                        rel="stylesheet"
                    />
                    <Routes>
                    {/* Auth */}
                    <Route path="/login" element={<LoginScreen />} />

                    {/* Main screens */}
                    <Route path="/loyalty" element={<LoyaltyAccessRoute><LoyaltyLayout /></LoyaltyAccessRoute>}>
                        <Route index element={<Navigate to="program" replace />} />
                        <Route path="program" element={<L><ProgramSettings /></L>} />
                        <Route path="program/setup" element={<L><ProgramSetup /></L>} />
                        <Route path="rewards" element={<L><RewardsCatalog /></L>} />
                        <Route path="rewards/:id/edit" element={<L><RewardForm /></L>} />
                        <Route path="members" element={<L><MembersPage /></L>} />
                        {/* DISABLED: <Route path="members/lapsed-rules" element={<L><MembersPage /></L>} /> */}
                        <Route path="members/:id" element={<L><MemberProfile /></L>} />
                        <Route path="notifications" element={<L><NotificationsList /></L>} />
                        <Route path="automated-rewards" element={<L><AutomatedRewardsList /></L>} />
                    </Route>
                    <Route path="/owner-view" element={<ProtectedRoute><OwnerViewProvider><OwnerViewLandingPage /></OwnerViewProvider></ProtectedRoute>} />
                    <Route path="/ai" element={<ProtectedRoute><AIScreen /></ProtectedRoute>} />
                    <Route path="/snapshot-m" element={<MobileV61 />} />
                    <Route path="/snapshot-d" element={<DesktopV61 />} />
                    <Route path="/product-d" element={<ProductV61 />} />

                    {/* Report screens under /reports/* */}
                    <Route path="/reports" element={<ProtectedRoute><ReportsScreen /></ProtectedRoute>} />
                    <Route path="/reports/snapshot" element={<ProtectedRoute><SnapshotScreen /></ProtectedRoute>} />
                    <Route path="/reports/sales" element={<ProtectedRoute><SalesScreen /></ProtectedRoute>} />
                    <Route path="/reports/product" element={<ProtectedRoute><ProductMenuPerformanceScreen /></ProtectedRoute>} />
                    <Route path="/reports/offers" element={<ProtectedRoute><OffersReportScreen /></ProtectedRoute>} />
                    <Route path="/reports/checkin" element={<ProtectedRoute><CheckinReport /></ProtectedRoute>} />
                    <Route path="/reports/labor" element={<ProtectedRoute><LaborReport /></ProtectedRoute>} />
                    <Route path="/reports/loyalty" element={<ProtectedRoute><LoyaltyReport /></ProtectedRoute>} />
                    <Route path="/reports/customers" element={<ProtectedRoute><CustomersReport /></ProtectedRoute>} />
                    <Route path="/reports/eod" element={<ProtectedRoute><EODSummaryReport /></ProtectedRoute>} />
                    <Route path="/reports/reservation" element={<ProtectedRoute><ReservationReport /></ProtectedRoute>} />
                    <Route path="/reports/events" element={<ProtectedRoute><EventReport /></ProtectedRoute>} />
                    <Route path="/docs" element={<ProtectedRoute><ReportsDocsPage /></ProtectedRoute>} />
                    <Route path="/employees" element={<ProtectedRoute><EmployeeLayout /></ProtectedRoute>}>
                        <Route index element={<EmployeeListPage />} />
                        <Route path="add" element={<EmployeeAddPage />} />
                        <Route path="edit/:id" element={<EmployeeEditPage />} />
                        <Route path="view/:id" element={<EmployeeViewPage />} />
                    </Route>
                    {/* Product Catalog — canonical no-id edit routes */}
                    <Route path="/product-catalog" element={<ProtectedRoute><PC><PCAvailabilityPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/items" element={<ProtectedRoute><PC><PCItemsPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/items/create" element={<ProtectedRoute><PC><PCItemFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/items/edit" element={<ProtectedRoute><PC><PCItemFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/menus" element={<ProtectedRoute><PC><PCMenusPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/menus/create" element={<ProtectedRoute><PC><PCMenuFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/menus/edit" element={<ProtectedRoute><PC><PCMenuFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/menus/:id/prices" element={<ProtectedRoute><PC><PCMenuPriceEditorPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/menus/performance" element={<ProtectedRoute><PC><PCMenuPerformanceReport /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/categories" element={<ProtectedRoute><PC><PCCategoriesPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/categories/create" element={<ProtectedRoute><PC><PCCategoryFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/categories/edit" element={<ProtectedRoute><PC><PCCategoryFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/modifiers" element={<ProtectedRoute><PC><PCModifiersPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/modifiers/create" element={<ProtectedRoute><PC><PCModifierFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/modifiers/edit" element={<ProtectedRoute><PC><PCModifierFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/variations" element={<ProtectedRoute><PC><PCVariationSetsPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/variations/create" element={<ProtectedRoute><PC><PCVariationSetFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/variations/edit" element={<ProtectedRoute><PC><PCVariationSetFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/option-sets" element={<Navigate to="/product-catalog/variations" replace />} />
                    <Route path="/product-catalog/option-sets/create" element={<Navigate to="/product-catalog/variations/create" replace />} />
                    <Route path="/product-catalog/option-sets/edit" element={<Navigate to="/product-catalog/variations/edit" replace />} />
                    <Route path="/product-catalog/combos" element={<ProtectedRoute><PC><PCCombosPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/combos/create" element={<ProtectedRoute><PC><PCComboFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/combos/edit" element={<ProtectedRoute><PC><PCComboFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/combos/report" element={<ProtectedRoute><PC><PCComboReportPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/set-menus" element={<ProtectedRoute><PC><PCSetMenusPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/availability" element={<ProtectedRoute><PC><PCAvailabilityPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/set-menus/create" element={<ProtectedRoute><PC><PCSetMenuFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/set-menus/edit" element={<ProtectedRoute><PC><PCSetMenuFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/kitchen-stations" element={<ProtectedRoute><PC><PCKitchenStationsPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/kitchen-stations/create" element={<ProtectedRoute><PC><PCKitchenStationFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/kitchen-stations/edit" element={<ProtectedRoute><PC><PCKitchenStationFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/sessions" element={<ProtectedRoute><PC><PCSessionsPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/sessions/create" element={<ProtectedRoute><PC><PCSessionFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/sessions/edit" element={<ProtectedRoute><PC><PCSessionFormPage /></PC></ProtectedRoute>} />
                    <Route path="/product-catalog/sync-history" element={<ProtectedRoute><PC><PCSyncHistoryPage /></PC></ProtectedRoute>} />

                    {/* Display */}
                    <Route path="/display" element={<ProtectedRoute><DisplayListPage /></ProtectedRoute>} />
                    <Route path="/display/create" element={<ProtectedRoute><CreateDisplay /></ProtectedRoute>} />
                    <Route path="/display/edit" element={<ProtectedRoute><CreateDisplay /></ProtectedRoute>} />
                    <Route path="/display/media-library" element={<ProtectedRoute><MediaLibraryPage /></ProtectedRoute>} />
                    <Route path="/display/media-library/playlists/create" element={<ProtectedRoute><PlaylistFormPage /></ProtectedRoute>} />

                    {/* Mobile and Desktop Report Screens - Reference */}
                    <Route path="/customers-m" element={<CustomersReportMobile />} />
                    <Route path="/customers-d" element={<CustomersReportdesktop />} />
                    <Route path="/checkin-m" element={<CheckinReportMobile />} />
                    <Route path="/checkin-d" element={<CheckinReportDesktop />} />
                    <Route path="/eod-d" element={<PaymentsAccountingDesktop />} />
                    <Route path="/eod-m" element={<PaymentsAccountingMobile />} />
                    <Route path="/labour-d" element={<LabourReportDesktop />} />
                    <Route path="/labour-m" element={<LabourReportMobile />} />
                    <Route path="/reservationdm" element={<ReservationDashboard />} />
                    <Route path="/event-d" element={<EventsPreOrderDesktop />} />
                    <Route path="/event-m" element={<EventsPreOrderMobile />} />
                    <Route path="/prod-m" element={<MobileSnapSaleProd />} />
                    <Route path="/multi-outlet-landing" element={<MultiOutletLanding />} />
                    <Route path="/reference-route-map" element={<ReferenceRouteMapPage />} />
                    <Route path="/sales-m" element={<SalesReference />} />
                    <Route path="/sales-d" element={<SalesReportDesktop />} />

                    {/* offers Routes */}
                       <Route path="/employees" element={<ProtectedRoute><EmployeeLayout /></ProtectedRoute>}></Route>
                       <Route path="/offers" element={<ProtectedRoute><PC><OffersListPage /></PC></ProtectedRoute>} />
                    <Route path="/offers/new" element={<ProtectedRoute><PC><OfferForm /></PC></ProtectedRoute>} />
                    <Route path="/offers/duplicate/:sourceId" element={<ProtectedRoute><PC><OfferForm /></PC></ProtectedRoute>} />
                    <Route path="/offers/:id/edit" element={<ProtectedRoute><PC><OfferForm /></PC></ProtectedRoute>} />
                    <Route path="/offers/:id" element={<ProtectedRoute><PC><OfferDetail /></PC></ProtectedRoute>} />
                     {/* Catering — hub & all routes (uses <C> = null-fallback Suspense, not LoyaltyLoader) */}
                    <Route path="/catering" element={<ProtectedRoute><C><CateringHubPage /></C></ProtectedRoute>} />
                    <Route path="/catering/settings" element={<ProtectedRoute><C><CateringSettingsPage /></C></ProtectedRoute>} />
                    <Route path="/catering/email-templates" element={<ProtectedRoute><C><CateringEmailTemplatesPage /></C></ProtectedRoute>} />
                    <Route path="/catering/packages" element={<ProtectedRoute><C><CateringPackagesPage /></C></ProtectedRoute>} />
                    <Route path="/catering/packages/create" element={<ProtectedRoute><C><CateringPackageFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/packages/:id/edit" element={<ProtectedRoute><C><CateringPackageFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders" element={<ProtectedRoute><C><CateringOrdersPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/create" element={<ProtectedRoute><C><CateringOrderFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/:id" element={<ProtectedRoute><C><CateringOrderDetailPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/:id/edit" element={<ProtectedRoute><C><CateringOrderFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/:id/beo" element={<ProtectedRoute><C><CateringOrderBEOPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/:id/pack-sheet" element={<ProtectedRoute><C><CateringOrderPackSheetPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/:id/labels" element={<ProtectedRoute><C><CateringContainerLabelsPage /></C></ProtectedRoute>} />
                    {/* <Route path="/catering/prep-list" element={<ProtectedRoute><C><CateringPrepListPage /></C></ProtectedRoute>} /> */}
                    <Route path="/catering/invoices" element={<ProtectedRoute><C><CateringInvoicesPage /></C></ProtectedRoute>} />
                    <Route path="/catering/invoices/create" element={<ProtectedRoute><C><CateringInvoiceFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/invoices/:id/edit" element={<ProtectedRoute><C><CateringInvoiceFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/invoices/:id" element={<ProtectedRoute><C><CateringInvoiceDetailPage /></C></ProtectedRoute>} />
                    <Route path="/catering/invoices/:id/review" element={<ProtectedRoute><C><CateringInvoiceReviewPage /></C></ProtectedRoute>} />
                    <Route path="/catering/calendar" element={<Navigate to="/catering/orders?view=month" replace />} />
                    <Route path="/catering/leads" element={<ProtectedRoute><C><CateringLeadsPage /></C></ProtectedRoute>} />
                    <Route path="/catering/leads/create" element={<ProtectedRoute><C><CateringLeadFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/leads/:id" element={<ProtectedRoute><C><CateringLeadDetailPage /></C></ProtectedRoute>} />
                    <Route path="/catering/leads/:id/edit" element={<ProtectedRoute><C><CateringLeadFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/leads/inquiry-form" element={<ProtectedRoute><C><CateringInquiryFormConfig /></C></ProtectedRoute>} />
                    <Route path="/catering/settings/beo-templates" element={<ProtectedRoute><C><CateringBeoTemplateEditor /></C></ProtectedRoute>} />
                    <Route path="/catering/estimates" element={<ProtectedRoute><C><CateringEstimatesPage /></C></ProtectedRoute>} />
                    <Route path="/catering/estimates/create" element={<ProtectedRoute><C><CateringEstimateFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/estimates/:id" element={<ProtectedRoute><C><CateringEstimateDetailPage /></C></ProtectedRoute>} />
                    <Route path="/catering/estimates/:id/edit" element={<ProtectedRoute><C><CateringEstimateFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/customers" element={<ProtectedRoute><C><CateringCustomersPage /></C></ProtectedRoute>} />
                    <Route path="/catering/customers/:id" element={<ProtectedRoute><C><CateringCustomerDetailPage /></C></ProtectedRoute>} />
                    <Route path="/catering/customers/:id/edit" element={<ProtectedRoute><C><CateringCustomerFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/recurring" element={<ProtectedRoute><C><CateringRecurringOrdersPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/recurring/create" element={<ProtectedRoute><C><CateringRecurringOrderFormPage /></C></ProtectedRoute>} />
                    <Route path="/catering/orders/recurring/:id/edit" element={<ProtectedRoute><C><CateringRecurringOrderFormPage /></C></ProtectedRoute>} />

                    {/* Public customer-facing estimate review page — no auth required */}
                    <Route path="/catering/estimates/:id/review" element={<C><CateringCustomerEstimatePage /></C>} />

                    {/* Default redirect */}
                    <Route
                        path="*"
                        element={<Navigate to={isAuthenticated ? homePath : "/login"} replace />}
                    />
                    </Routes>
                    {renderReportsChatbot && enableChatbotReportsBoolean && (
                        <ChatbotReports
                            headerTextReportsChatBot="Maghil Insights"
                            centerTextReportsChatBot="Ask about sales, employees, refunds and trends."
                            messageInputPlaceholderReportsChatBot="Ask me anything..."
                            footerTextReportsChatBot="AI may make mistakes, Check your important info"
                            enableSuggestionsReportsChatBot={true}
                            enableVoiceInputReportsChatBot={false}
                            enableFileUploadReportsChatBot={false}
                        />
                    )}
                    <ProductCatalogToastContainer />
                    <OffersToastContainer />
                    <CateringToastContainer />
                </div>
            </DesktopLayoutProvider>
        </BranchProvider>
    );
};

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <NavigationGuardProvider>
                    <AppRoutes />
                </NavigationGuardProvider>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
